/*
 * Copyright 2026 Sony Corporation
 */

import { RUNNING_STATUSES, type Asset } from '@ips-device-verse/twin-service/schema/assets';

/**
 * How My Library reads a user's assets (3d-reconstruction.md §9).
 *
 * A reconstruction's splat is shown under the video it came from. Everything
 * else is a plain tile.
 */

export interface Reconstruction {
  reconstruction_id: string;
  source?: Asset;
  splat: Asset;
  started_at: string;
}

export interface LibraryView {
  reconstructions: Reconstruction[];
  others: Asset[];
}

export const isRunning = (asset: Asset): boolean => RUNNING_STATUSES.includes(asset.status);

/** Newest first, as the service lists them. A video stays a plain tile: it is what Reconstruct is pressed on. */
export const viewOf = (assets: readonly Asset[]): LibraryView => {
  const byId = new Map(assets.map((asset) => [asset.asset_id, asset]));
  const reconstructions: Reconstruction[] = [];
  const others: Asset[] = [];

  for (const asset of assets) {
    if (!asset.reconstruction_id || asset.kind !== 'splat') {
      others.push(asset);
      continue;
    }
    reconstructions.push({
      reconstruction_id: asset.reconstruction_id,
      source: asset.source_asset_id ? byId.get(asset.source_asset_id) : undefined,
      splat: asset,
      started_at: asset.created_at,
    });
  }
  return { reconstructions, others };
};

const STAGES: Partial<Record<Asset['status'], string>> = {
  queued: 'Queued',
  preparing: 'Preparing the video',
  training: 'Training the splat',
  finalizing: 'Finishing',
};

const minutes = (since: string, now: number): string => {
  const elapsed = Math.max(0, Math.floor((now - Date.parse(since)) / 60_000));
  return elapsed < 1
    ? 'just now'
    : elapsed < 60
      ? `${elapsed} min`
      : `${Math.floor(elapsed / 60)} h ${elapsed % 60} min`;
};

/**
 * One line under a tile: the stage, what it is waiting on, and for how long.
 *
 * The wait is said out loud. A GPU once took 1 h 47 min to arrive, and a
 * stage name alone over that long reads as a stall.
 */
export const statusLine = (asset: Asset, now: number): string | undefined => {
  if (asset.status === 'failed') {
    return asset.failure ?? 'Failed.';
  }
  if (asset.status === 'uploading') {
    return 'The upload did not finish.';
  }
  const stage = STAGES[asset.status];
  if (!stage) {
    return undefined;
  }
  const job = asset.job;
  if (job?.phase === 'waiting') {
    return `${stage} · waiting for another reconstruction to finish · ${minutes(job.since, now)}`;
  }
  if (job?.phase === 'starting') {
    // `prepare` runs on a CPU instance; only training waits for a GPU.
    const machine = asset.status === 'preparing' ? 'a machine' : 'a GPU';
    return `${stage} · waiting for ${machine} · ${minutes(job.since, now)}`;
  }
  return `${stage} · ${minutes(job?.since ?? asset.updated_at, now)}`;
};

export const anyRunning = (assets: readonly Asset[]): boolean => assets.some(isRunning);
